import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Stack,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { parsePrompt } from "../UIUtils";

// ── constants ─────────────────────────────────────────────────────────────────
const PRIMARY_COLOR = "#B87333"; // Copper
const BORDER_COLOR  = "#E2E8F0"; // Slate 200
const ACTIVE_STATUS_LABEL = "processing";

const normalizeRunStatus = (status?: string | null) =>
  String(status || "").trim().toLowerCase();

const displayRunStatus = (status?: string | null) => {
  const normalized = normalizeRunStatus(status);
  switch (normalized) {
    case "success":
    case "completed":
      return "completed";
    case "error":
    case "failed":
      return "failed";
    case "queued":
    case "pending":
      return "queued";
    case "processing":
    case "running":
      return ACTIVE_STATUS_LABEL;
    default:
      return normalized || "—";
  }
};

const runStatusColor = (status?: string) => {
  switch (normalizeRunStatus(status)) {
    case "completed": case "success":  return "#5FA85A";
    case "failed":    case "error":    return "#f44336";
    case "queued":    case "pending":  return "rgba(184, 115, 51, 0.58)";
    case "processing":case "running":  return PRIMARY_COLOR;
    default:                           return "#9e9e9e";
  }
};

const fmt = (iso?: string | null) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso!; }
};

interface HistorySidebarProps {
  isOpen: boolean;
  agentRuns: any[];
  selectedRunNumber: number | null;
  selectedRunKey?: string | null;
  hasMoreHistory: boolean;
  isLoadingMoreHistory: boolean;
  totalHistoryRuns: number;
  onToggleClose: () => void;
  onSelectRun: (run: any) => void;
  onLoadMore: () => void;
  onRefresh?: () => void;
  onRenameRun?: (run: any, newLabel: string) => Promise<void>;
  onFavoriteRun?: (run: any) => void;
  favoriteRunPendingKeys?: Set<string>;
  fullWidth?: boolean;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({
  isOpen,
  agentRuns,
  selectedRunNumber,
  selectedRunKey,
  hasMoreHistory,
  isLoadingMoreHistory,
  totalHistoryRuns,
  onToggleClose,
  onSelectRun,
  onLoadMore,
  onRefresh,
  onRenameRun,
  onFavoriteRun,
  favoriteRunPendingKeys,
  fullWidth = false,
}) => {
  // ── inline rename state ──────────────────────────────────────────────────
  const [editingRunKey, setEditingRunKey] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [historyView, setHistoryView] = React.useState<"recent" | "favorites" | "all">("recent");
  const [searchQuery, setSearchQuery] = React.useState("");

  const startEdit = (e: React.MouseEvent, run: any) => {
    e.stopPropagation();
    setEditingRunKey(runKey(run));
    setEditValue(run.run_label ?? `Run #${run.run_number}`);
  };

  const cancelEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingRunKey(null);
    setEditValue("");
  };

  const commitEdit = async (e: React.MouseEvent | React.KeyboardEvent, run: any) => {
    e.stopPropagation();
    if (!onRenameRun) return;
    setIsSaving(true);
    try {
      await onRenameRun(run, editValue.trim());
    } finally {
      setIsSaving(false);
      setEditingRunKey(null);
      setEditValue("");
    }
  };

  const runKey = (run: any) =>
    `${run?.agent_version ?? run?.version ?? "v"}:${run?.run_number ?? "run"}`;

  const runTitle = (run: any, prompt: string) => {
    const explicit = String(run?.run_label || "").trim();
    if (explicit) return explicit;
    const trimmedPrompt = String(prompt || "").trim();
    if (trimmedPrompt) return trimmedPrompt;
    return `Attempt ${run.run_number}`;
  };

  const favoriteCount = agentRuns.filter((run) => Boolean(run.favorite)).length;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredRuns = agentRuns.filter((run, index) => {
    const prompt = parsePrompt(run?.agent_prompt ?? "");
    const title = runTitle(run, prompt);
    const searchMatch =
      !normalizedSearch ||
      title.toLowerCase().includes(normalizedSearch) ||
      prompt.toLowerCase().includes(normalizedSearch) ||
      String(run.run_number || "").includes(normalizedSearch);
    const viewMatch =
      historyView === "all" ||
      (historyView === "favorites" ? Boolean(run.favorite) : normalizedSearch ? true : index < 12);
    return searchMatch && viewMatch;
  });

  const viewButtonSx = (active: boolean) => ({
    textTransform: "none",
    borderRadius: 999,
    px: 1.15,
    py: 0.35,
    minWidth: "auto",
    fontSize: "0.7rem",
    fontWeight: 750,
    color: active ? PRIMARY_COLOR : "#64748B",
    border: `1px solid ${active ? "rgba(184, 115, 51, 0.42)" : "rgba(226, 232, 240, 0.95)"}`,
    bgcolor: active ? "rgba(184, 115, 51, 0.08)" : "#FFFFFF",
    "&:hover": {
      borderColor: "rgba(184, 115, 51, 0.58)",
      bgcolor: "rgba(184, 115, 51, 0.07)",
    },
  });

  return (
    <>
      {/* LEFT SIDEBAR */}
      <Box
        sx={{
          width: isOpen ? (fullWidth ? "100%" : { xs: "220px", md: "260px" }) : "0px",
          minWidth: isOpen ? (fullWidth ? "100%" : { xs: "220px", md: "260px" }) : "0px",
          maxWidth: isOpen ? (fullWidth ? "100%" : { xs: "220px", md: "260px" }) : "0px",
          transition: "width 0.3s ease",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "white",
          borderRight: isOpen ? `1px solid ${BORDER_COLOR}` : "none",
          mt: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <Tooltip title="Click to refresh" placement="right" arrow>
            <Box
              onClick={onRefresh}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                cursor: onRefresh ? "pointer" : "default",
                borderRadius: 1,
                "&:hover": onRefresh ? { opacity: 0.7 } : {},
              }}
            >
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: "text.disabled", letterSpacing: 1.2, lineHeight: 1 }}
              >
                Run History
              </Typography>
            </Box>
          </Tooltip>
          <IconButton size="small" onClick={onToggleClose} sx={{ color: "text.disabled", p: 0.25 }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.4, py: 1.15, borderBottom: `1px solid ${BORDER_COLOR}` }}>
          <TextField
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search runs"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 0.9,
              "& .MuiOutlinedInput-root": {
                borderRadius: "999px",
                bgcolor: "#F8FAFC",
                fontSize: "0.78rem",
                "& fieldset": { borderColor: "rgba(226, 232, 240, 0.9)" },
                "&:hover fieldset": { borderColor: "rgba(184, 115, 51, 0.45)" },
                "&.Mui-focused fieldset": { borderColor: PRIMARY_COLOR },
              },
            }}
          />
          <Stack direction="row" spacing={0.55} alignItems="center">
            <Button size="small" onClick={() => setHistoryView("recent")} sx={viewButtonSx(historyView === "recent")}>
              Recent
            </Button>
            <Button size="small" onClick={() => setHistoryView("favorites")} sx={viewButtonSx(historyView === "favorites")}>
              Favorites{favoriteCount ? ` ${favoriteCount}` : ""}
            </Button>
            <Button size="small" onClick={() => setHistoryView("all")} sx={viewButtonSx(historyView === "all")}>
              All
            </Button>
          </Stack>
        </Box>

        {/* Run list */}
        <List
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 1,
            py: 0.5,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.08)",
              borderRadius: "10px",
            },
          }}
        >
          {filteredRuns.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 650 }}>
                {historyView === "favorites" ? "No favorite runs yet" : "No matching runs"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 0.5 }}>
                {historyView === "favorites"
                  ? "Use the heart on a strong result to save it here."
                  : "Try a different search or view."}
              </Typography>
            </Box>
          )}
          {filteredRuns.map((run) => {
            const currentRunKey = runKey(run);
            const isSelected = selectedRunKey
              ? selectedRunKey === currentRunKey
              : selectedRunNumber === run.run_number;
            const prompt = parsePrompt(run?.agent_prompt ?? "");
            const title = runTitle(run, prompt);
            const isFavorite = Boolean(run.favorite);
            const isFavoritePending = Boolean(favoriteRunPendingKeys?.has(currentRunKey));
            return (
            <ListItem key={currentRunKey} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => onSelectRun(run)}
                  sx={{
                    py: 1.35,
                    px: 1.55,
                    borderRadius: "16px",
                    transition: "all 0.15s",
                    border: "1px solid transparent",
                    "&.Mui-selected": {
                      bgcolor: "rgba(184, 115, 51, 0.08)",
                      borderColor: "rgba(184, 115, 51, 0.24)",
                      borderLeft: `4px solid ${PRIMARY_COLOR}`,
                      boxShadow: "0 10px 24px rgba(17, 24, 39, 0.05)",
                      "&:hover": { bgcolor: "rgba(184, 115, 51, 0.11)" },
                    },
                    "&:hover": {
                      bgcolor: "rgba(15, 23, 42, 0.035)",
                    },
                  }}
                >
                  <Stack spacing={0.55} sx={{ width: "100%", overflow: "hidden" }}>
                    {/* Run label / inline edit + status dot */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                      {editingRunKey === currentRunKey ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                          <TextField
                            size="small"
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && editValue.trim()) commitEdit(e, run);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            inputProps={{ maxLength: 100 }}
                            sx={{
                              flex: 1,
                              "& .MuiInputBase-input": { fontSize: "0.78rem", py: "3px", px: "6px" },
                              "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                            }}
                          />
                          <IconButton size="small" disabled={isSaving || !editValue.trim()} onClick={(e) => commitEdit(e, run)} sx={{ p: 0.3 }}>
                            <CheckIcon sx={{ fontSize: "0.9rem", color: editValue.trim() ? "#16a34a" : "#cbd5e1" }} />
                          </IconButton>
                          <IconButton size="small" disabled={isSaving} onClick={(e) => { e.stopPropagation(); cancelEdit(); }} sx={{ p: 0.3 }}>
                            <CloseIcon sx={{ fontSize: "0.9rem", color: "#dc2626" }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          onDoubleClick={(e) => {
                            if (onRenameRun) startEdit(e, run);
                          }}
                          sx={{
                            display: "flex", alignItems: "center", justifyContent: "flex-start",
                            flex: 1, minWidth: 0, gap: 0.8,
                            cursor: onRenameRun ? "text" : "default",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: isSelected ? 750 : 650, color: isSelected ? PRIMARY_COLOR : "#334155", fontSize: "0.84rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {title}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.45, flexShrink: 0 }}>
                        {onFavoriteRun && (
                          <Tooltip title={isFavorite ? "Saved to Mnemo" : "Favorite this run"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={isFavorite || isFavoritePending}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onFavoriteRun(run);
                                }}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  p: 0,
                                  color: isFavorite ? PRIMARY_COLOR : "#94A3B8",
                                  opacity: isFavoritePending ? 0.55 : 1,
                                  "&:hover": {
                                    bgcolor: "rgba(184, 115, 51, 0.08)",
                                    color: PRIMARY_COLOR,
                                  },
                                }}
                              >
                                {isFavorite ? (
                                  <FavoriteIcon sx={{ fontSize: "0.95rem" }} />
                                ) : (
                                  <FavoriteBorderIcon sx={{ fontSize: "0.95rem" }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: runStatusColor(run.status), flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                          {displayRunStatus(run.status)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Timestamp */}
                    <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem" }}>
                      Attempt {run.run_number} · {fmt(run.started_at ?? run.created_at)}
                    </Typography>

                    {/* Prompt snippet */}
                    {prompt && title !== prompt && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748B",
                          fontSize: "0.72rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {prompt}
                      </Typography>
                    )}
                  </Stack>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* Footer */}
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${BORDER_COLOR}` }}>
          {hasMoreHistory ? (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={onLoadMore}
              disabled={isLoadingMoreHistory}
              sx={{
                textTransform: "none",
                borderRadius: 99,
                fontSize: "0.72rem",
                borderColor: BORDER_COLOR,
                color: "text.secondary",
                "&:hover": { borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR },
              }}
            >
              {isLoadingMoreHistory ? (
                <><CircularProgress size={12} sx={{ mr: 0.75 }} />Loading...</>
              ) : (
                `Load more (${agentRuns.length} / ${totalHistoryRuns})`
              )}
            </Button>
          ) : (
            <Typography variant="caption" sx={{ textAlign: "center", display: "block", color: "text.disabled" }}>
              {agentRuns.length > 0 ? `All ${totalHistoryRuns} runs loaded` : "No runs yet"}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default HistorySidebar;
